import { zoneColors } from './constants';
import { WorkoutStatus, type Workout } from './types';

export { zoneColors };

export type ZoneColorKey = keyof typeof zoneColors;

export const getZoneColor = (zone: number = 1): string => {
  const zoneKey = `zone-${zone}` as ZoneColorKey;
  return zoneColors[zoneKey] || zoneColors['zone-1'];
};

// Client-safe workout builders (without server-side CreatedSource dependency)
const buildWorkout = (overrides: Partial<Workout>): Workout => ({
  id: '',
  name: '',
  description: '',
  steps: [],
  distance: null,
  duration: null,
  date: new Date(),
  sportType: 'Run',
  userId: '',
  status: WorkoutStatus.PENDING,
  createdSource: null, // Client doesn't need specific CreatedSource
  isUserEdited: false,
  feeling: null,
  rpe: null,
  notes: null,
  createdAt: new Date(),
  updatedAt: new Date(),
  garminWorkoutId: null,
  garminScheduleId: null,
  planInstanceId: null,
  weekIndex: null,
  workoutKey: null,
  ...overrides,
});

export const defaultWorkout = buildWorkout({
  name: 'TrainMe Workout',
  description: 'Base run. Created by Trainme.',
  distance: 5000,
  duration: 1800,
  steps: ['5m Z2 HR Warm up', '20m Z3 HR active', '5m Z1 HR cool down'],
  createdSource: 'USER_APP', // Use string literal instead of enum
});

export const emptyWorkout = buildWorkout({});

// Heart rate zone calculation
export const getZoneFromHeartRate = (
  heartRate: number,
  zones: { zone: number; minValue: number; maxValue: number }[]
): number => {
  const zone = zones.find(
    (zone) => heartRate >= zone.minValue && heartRate <= zone.maxValue
  );
  return zone?.zone || 1;
};

// Sport icon utilities
export type SportIconKey =
  | 'run'
  | 'ride'
  | 'swim'
  | 'weightTraining'
  | 'yoga'
  | 'walk'
  | 'hike'
  | 'rowing'
  | 'other';

export type SportIconMeta = {
  icon: SportIconKey;
  color: string;
};

const sportIconConfig: Record<string, SportIconMeta> = {
  Run: { icon: 'run', color: '#dc2626' },
  TrailRun: { icon: 'run', color: '#dc2626' },
  VirtualRun: { icon: 'run', color: '#dc2626' },
  Ride: { icon: 'ride', color: '#e5be00' },
  VirtualRide: { icon: 'ride', color: '#e5be00' },
  Swim: { icon: 'swim', color: '#0284c7' },
  OpenWaterSwim: { icon: 'swim', color: '#0284c7' },
  Rowing: { icon: 'rowing', color: '#1ca3d8' },
  VirtualRowing: { icon: 'rowing', color: '#1ca3d8' },
  WeightTraining: { icon: 'weightTraining', color: '#f97316' },
  Yoga: { icon: 'yoga', color: '#a855f7' },
  Walk: { icon: 'walk', color: '#ec4899' },
  Hike: { icon: 'hike', color: '#15803d' },
  Other: { icon: 'other', color: '#dc2626' },
};

const defaultSportIcon: SportIconMeta = sportIconConfig.Run;

export const getSportIconMeta = (sportType?: string): SportIconMeta => {
  if (!sportType) return defaultSportIcon;
  return sportIconConfig[sportType] || defaultSportIcon;
};

export const getSportIconColor = (sportType?: string): string =>
  getSportIconMeta(sportType).color;
